import { useCallback, useEffect, useRef } from 'react';

import { useRouter, useRouterState } from '@tanstack/react-router';

import { FLASH_DURATION_MS } from './flash';
import { useSingleRoot } from './SingleRootContext';

import type { AnyRouter, ParsedLocation } from '@tanstack/react-router';

import type { SnackbarSetter, SnackbarState } from '@/components/NewUI/ComponentsLibrary';

/** `durationMs` overrides the snackbar's 2 seconds inside the shell. */
export type ShellNotice = (message: string, state?: SnackbarState, durationMs?: number) => void;

/**
 * How a page tells the user something. Inside the shell the notice goes to the shell's one
 * snackbar. Outside it (a page still mounted from HAML) it goes to `fallback`, the page's own
 * snackbar setter, so that page renders its own host; with no fallback it does nothing.
 */
export const useShellNotice = (fallback?: SnackbarSetter): ShellNotice => {
  const notify = useSingleRoot()?.notify;
  return useCallback(
    (message, state, durationMs) => {
      const input = { message, state: state ?? 'success' };
      if (notify) notify(durationMs === undefined ? input : { ...input, durationMs });
      else fallback?.(input);
    },
    [fallback, notify]
  );
};

// A redirect route's notice rides in the history state of the location it redirects to.
declare module '@tanstack/history' {
  interface HistoryState {
    singleRootNotice?: string;
  }
}

export const noticeState = (message: string): { singleRootNotice: string } => ({
  singleRootNotice: message
});

/**
 * Shows a resolved location's redirect notice once, as a flash would, then drops it from that
 * history entry so a reload of, or a return to, the entry does not show it again.
 */
const useLocationNotice = (): void => {
  const router = useRouter<AnyRouter>();
  const location = useRouterState<AnyRouter, ParsedLocation | undefined>({
    select: (state) => state.resolvedLocation
  });
  const notice = useShellNotice();
  const shown = useRef<string | null>(null);
  const message = location?.state.singleRootNotice ?? null;
  const entry = location?.state.key ?? null;

  useEffect(() => {
    if (!message || !entry || shown.current === entry) return;
    shown.current = entry;
    notice(message, 'warning', FLASH_DURATION_MS);
    // The page may have moved on already; its own entry carries no notice.
    const current = router.history.location;
    if (current.state.key !== entry) return;
    // History assigns the entry a fresh key and keeps its index.
    const { singleRootNotice: consumed, ...state } = current.state;
    router.history.replace(current.href, state, { ignoreBlocker: true });
  }, [entry, message, notice, router]);
};

// Its own component, so a resolved location re-renders this and not the shell's chrome.
export const LocationNotice = (): null => {
  useLocationNotice();
  return null;
};
